import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';

import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { LandingModule, LandingModuleLink, LandingPageConfig } from './landing-page-types';

/**
 * Pure renderer for a landing page config — used by BOTH the published view
 * (`/dvm-published/<slug>` when the layout_json is a landing page) and the
 * landing builder's live preview. In the preview (`interactive` = false)
 * clicks never navigate; they emit `moduleSelect` so the editor can jump the
 * form to the clicked module.
 */
@Component({
  selector: 'app-dvm-landing-page',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './dvm-landing-page.component.html',
  styleUrl: './dvm-landing-page.component.scss',
})
export class DvmLandingPageComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly ui = inject(SelfServiceUiService);
  protected readonly projectMode = (environment as any).PROJECT_MODE === true
    || (environment as any).PROJECT_MODE === 'true';

  @Input({ required: true }) config!: LandingPageConfig;
  /** False in the builder preview — clicks select the module instead of navigating. */
  @Input() interactive = true;
  /** Published Edit Layout mode: cards get a drag handle to resize their grid
   *  span (normal → wide → full); links go inert while it's on. */
  @Input() layoutEdit = false;
  @Output() moduleSelect = new EventEmitter<number>();

  /** Ids of the sub-menu groups currently expanded (collapsed by default). */
  private readonly expandedGroups = new Set<string>();

  onModuleCardClick(index: number): void {
    if (this.layoutEdit) return;
    if (!this.interactive) this.moduleSelect.emit(index);
  }

  hasChildren(link: LandingModuleLink): boolean {
    return !!link?.children?.length;
  }

  isExpanded(link: LandingModuleLink): boolean {
    return this.expandedGroups.has(link.id);
  }

  /** A button WITH children is a pure expand/collapse toggle — it never
   *  navigates (works in the preview too, so submenus can be inspected). */
  toggleGroup(link: LandingModuleLink, event: MouseEvent): void {
    event.stopPropagation();
    if (this.layoutEdit) return;
    if (this.expandedGroups.has(link.id)) this.expandedGroups.delete(link.id);
    else this.expandedGroups.add(link.id);
  }

  /** Grid placement for a module card's configured size. */
  moduleGridColumn(module: LandingModule): string | null {
    if (module?.size === 'wide') return 'span 2';
    if (module?.size === 'full') return '1 / -1';
    return null;
  }

  /** Size label shown on the card while Edit Layout is on. */
  moduleSizeLabel(module: LandingModule): string {
    const width = module?.size === 'full' ? 'Full width' : module?.size === 'wide' ? '2 columns' : '1 column';
    return module?.minHeight ? `${width} · ${module.minHeight}px` : width;
  }

  // ── Edit-Layout drag-resize ───────────────────────────────────────────────
  // Dragging the corner handle horizontally snaps the card between the grid's
  // real sizes (1 column / 2 columns / full row) — a free pixel resize would
  // fight the CSS grid, so we resize in track units like the dashboard editor
  // resizes in grid cells.
  private cardResize: {
    index: number;
    startX: number;
    startY: number;
    startSpan: number;
    startHeight: number;
    colWidth: number;
    gap: number;
    trackCount: number;
    /** True once the drag has clearly moved vertically — before that, small
     *  jitter during a horizontal (width) drag must not stamp a min-height. */
    heightEngaged: boolean;
  } | null = null;

  /** Below this a dragged min-height is dropped entirely — the card returns to
   *  auto/content height (that's how a user "resets" the height: drag up). */
  private static readonly MIN_CARD_HEIGHT_PX = 150;
  private static readonly MAX_CARD_HEIGHT_PX = 1200;

  startCardResize(index: number, event: PointerEvent): void {
    if (!this.layoutEdit) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.target as HTMLElement;
    const card = handle.closest('.landing-module-card') as HTMLElement | null;
    const grid = card?.closest('.landing-module-grid') as HTMLElement | null;
    if (!card || !grid) return;
    const gridStyles = getComputedStyle(grid);
    const gap = parseFloat(gridStyles.columnGap) || 18;
    // The RESOLVED value lists the actual track sizes ("300px 300px 300px"),
    // so its length is the real current column count at this viewport width.
    const trackCount = Math.max(1, gridStyles.gridTemplateColumns.split(' ').filter(Boolean).length);
    const colWidth = (grid.clientWidth - gap * (trackCount - 1)) / trackCount;
    const module = this.config.modules[index];
    const startSpan = module?.size === 'full' ? trackCount : module?.size === 'wide' ? Math.min(2, trackCount) : 1;
    this.cardResize = {
      index,
      startX: event.clientX,
      startY: event.clientY,
      startSpan,
      startHeight: card.offsetHeight,
      colWidth,
      gap,
      trackCount,
      heightEngaged: false,
    };
    window.addEventListener('pointermove', this.onCardResizeMove);
    window.addEventListener('pointerup', this.onCardResizeUp);
    window.addEventListener('pointercancel', this.onCardResizeUp);
  }

  private onCardResizeMove = (event: PointerEvent): void => {
    const r = this.cardResize;
    if (!r) return;
    const module = this.config.modules[r.index];
    if (!module) return;

    // Horizontal: snap the column span (1 / 2 / full row).
    const dx = event.clientX - r.startX;
    const span = Math.min(r.trackCount, Math.max(1, r.startSpan + Math.round(dx / (r.colWidth + r.gap))));
    const next: LandingModule['size'] = span >= r.trackCount && r.trackCount > 1 ? 'full' : span >= 2 ? 'wide' : undefined;
    if (module.size !== next) {
      if (next) module.size = next;
      else delete module.size;
    }

    // Vertical: continuous min-height. Dragging up below the floor clears it,
    // returning the card to auto/content height.
    const dy = event.clientY - r.startY;
    if (!r.heightEngaged && Math.abs(dy) < 8) return;
    r.heightEngaged = true;
    const height = Math.round((r.startHeight + dy) / 4) * 4;
    if (height < DvmLandingPageComponent.MIN_CARD_HEIGHT_PX) {
      delete module.minHeight;
    } else {
      module.minHeight = Math.min(DvmLandingPageComponent.MAX_CARD_HEIGHT_PX, height);
    }
  };

  private onCardResizeUp = (): void => {
    this.cardResize = null;
    window.removeEventListener('pointermove', this.onCardResizeMove);
    window.removeEventListener('pointerup', this.onCardResizeUp);
    window.removeEventListener('pointercancel', this.onCardResizeUp);
  };

  ngOnDestroy(): void {
    // A navigation mid-drag would leak the window listeners.
    this.onCardResizeUp();
  }

  openLink(link: LandingModuleLink, moduleIndex: number, event: MouseEvent): void {
    // Keep the card's own click handler from double-firing in preview mode.
    event.stopPropagation();
    if (this.layoutEdit) return;
    if (!this.interactive) {
      this.moduleSelect.emit(moduleIndex);
      return;
    }
    const target = String(link?.target ?? '').trim();
    if (!target) {
      // A dead button is a config gap, not an error state — say so instead of
      // silently ignoring the click (the trap: adding a button in the editor
      // without picking its target).
      this.ui.showError(`"${link.label}" has no page configured yet — open the landing page editor and set this button's target.`);
      return;
    }
    if (link.targetType === 'url' || /^https?:\/\//i.test(target)) {
      window.open(target, '_blank', 'noopener');
      return;
    }
    const path = link.targetType === 'dashboard'
      ? `/dvm-published/${target.replace(/^\/+/, '')}`
      : (target.startsWith('/') ? target : `/${target}`);
    void this.router.navigateByUrl(path);
  }
}
